import { Injectable, inject } from '@angular/core';
import {forkJoin, Observable, of, zip} from 'rxjs';
import {map, shareReplay, switchMap} from 'rxjs/operators';
import {DatasetDetail, DatasetType} from './models/datasets/dataset-type';
import {ODataCollectionResult} from '../odata-collection-result';
import {HttpClient} from '@angular/common/http';
import {Dataset, DatasetConfig} from './models/datasets/dataset';
import {DatasetSource} from './models/datasets/dataset-source';
import {DatasetResponse} from './models/datasets/dataset-response';
import {Data} from '@angular/router';
import {LoggerService} from '../../logger.service';
import {Share, ShareMode} from '../share';
import {GovernmentId} from './models/government-id';
import {GovernmentSpecificity} from './models/government-specificity';
import {FitApiService} from './fit-api.service';
import {MCAG} from './models/mcag';
import {GovTypeCode} from './models/gov-type-code';
import {GovernmentType} from "./models/government-type";
import {intersection} from "../../functions/array/intersection";
import {SnapshotWithDetail} from './models/datasets/snapshot';
import {LiveWithDetail} from './models/datasets/live';
import {SchoolsWithDetail} from './models/datasets/schools';

@Injectable({
	providedIn: 'root'
})
export class DatasetService {
	private httpClient = inject(HttpClient);
	private logger = inject(LoggerService);
	private api = inject(FitApiService);

	// Default number of years to display in multi-year "trend" scenarios
	static DEFAULT_TREND_YEARS = 4;

	/**
	 * Get all Datasets and cache. This will return anything you have access to as the currently-logged-in user.
	 */
	readonly datasets: Observable<Array<Dataset>> = this.getDatasets().pipe(
			shareReplay(1) // Cache for life of app
		);

	// DatasetType to detail model map
	readonly detailModels = {
		[DatasetType.Snapshot]: SnapshotWithDetail,
		[DatasetType.Live]: LiveWithDetail,
		[DatasetType.Schools]: SchoolsWithDetail,
	};

	getDatasets():Observable<Array<Dataset>> {
		return this.httpClient
			.get<ODataCollectionResult<DatasetResponse>>(`Datasets`)
			.pipe(map(result =>
				result.value.map(dataset => new Dataset(dataset))
			));
	}

	/**
	 * Get the default Dataset metadata for the given DatasetSource.<br />
	 *  For versioned DatasetTypes, this will return the Dataset containing the max of `Dataset.id` within the
	 *  DatasetType, otherwise the singleton Dataset for the DatasetType.
	 * @param source
	 * @param id
	 */
	getDataset(source: DatasetSource, id?: never): Observable<Dataset | null>;
	/**
	 * Get the Dataset metadata for the given DatasetType.
	 * @param type
	 * @param id For versioned DatasetTypes, id will be used to retrieve the specific Dataset. If not provided,
	 *  the Dataset containing the max of id within the DatasetType will be returned.
	 */
	getDataset(type: DatasetType, id?: number): Observable<Dataset>;
	getDataset(sourceOrType: DatasetSource | DatasetType, id?: number): Observable<Dataset | null> {
		let datasetType: DatasetType | null = null;
		if (sourceOrType in DatasetType) {
			// DatasetType was already provided
			datasetType = sourceOrType as DatasetType;
		} else if (sourceOrType in DatasetSource) {
			// todo break into own resolution function
			// DatasetSource provided; resolve the default DatasetType
			// Natural order determines default, i.e. first in config matching on DatasetSource is the default
			if (sourceOrType !== DatasetSource.None) {
				for (const [, config] of Object.entries(DatasetConfig)) {
					if (config.datasetSource === sourceOrType) {
						datasetType = config.datasetType;
						break;
					}
				}
			}
		} else  {
			throw new Error(`value ${sourceOrType} is neither a DatasetSource nor DatasetType`);
		}

		if (datasetType) {
			// Search the cached Datasets for a match
			return this.datasets.pipe(
				map(datasets => {
					const datasetsOfType = datasets.filter(x => x.datasetType === datasetType);
					if (!datasetsOfType) {
						throw new Error(`Could not locate any Datasets of type ${datasetType}`);
					}

					const datasetTypeConfig = DatasetConfig[datasetType ?? DatasetType.Snapshot];
					let dataset: Dataset | null;
					if (datasetTypeConfig.versioned) {
						if (id !== undefined) {
							const datasetWithId = datasetsOfType.find(x => x.id === id);
							if (!datasetWithId) {
								throw new Error(`Could not locate Dataset for ${datasetType} ${id}.`);
							}
							dataset = datasetWithId;
						} else {
							// find the Dataset with max id
							dataset = datasetsOfType.reduce((prev, current) =>
								// ids cannot be null for versioned Dataset types
								// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
								current.id! > prev.id! ? current : prev
							);
						}
					} else {
						// Otherwise pick the first one (which should be the only one for a singleton type).
						dataset = datasetsOfType[0];
						// Note that this may return undefined if you are attempting to access a Dataset that you do not
						//  have access to (i.e. Live, Schools) since they are removed from the API response.
						if (dataset === undefined) {
							this.logger.info(`Could not locate Dataset for ${datasetType} or user doesn't have access to the specified dataset.`);
							return dataset = null;
						}
					}
					return dataset;
				})
			);
		}
		return of(null);
	}

	/**
	 * Get a specific DatasetWithDetail.
	 * @param datasetType
	 * @param id
	 * @param withDetail
	 */
	@Share(ShareMode.Replay)
	getDatasetDetail(datasetType: DatasetType, id?: number, withDetail = true): Observable<DatasetDetail> {
		return this.getDataset(datasetType, id).pipe(
			switchMap(dataset => {
				const detailModel = this.detailModels[datasetType];
				const expand = withDetail ? `?$expand=Detail` : '';
				// Need to know the type to know the response type to use (any)
				return this.httpClient.get<any>(`${dataset.route}${expand}`).pipe(
					map(result => new detailModel(result))
				);
			})
		);
	}

	/**
	 * Uses the Dataset object populated in ActivatedRoute.data to fetch the full DatasetDetail.
	 * @param routeData
	 */
	getDatasetDetailFromRoute(routeData: Observable<Data>): Observable<DatasetDetail | null> {
		return routeData.pipe(
			map(data => {
				const dataset = data['dataset'] as Dataset;
				if (dataset === undefined) {
					throw new Error(`No Dataset present in route data.`);
				}
				return dataset;
			}),
			// This returns null because there are conditions where you are not allowed access to a Dataset, however
			// the page still needs to be displayed. (E.g. Schools, but you aren't logged in.)
			switchMap(dataset => dataset ?
				this.getDatasetDetail(dataset.datasetType, dataset.id) :
				of(null)
			)
		);
	}

	/**
	 * Retrieves the default Dataset based on GovernmentId, or null if GovernmentId cannot be resolved.
	 * @param {GovernmentId} governmentId
	 * @returns {Observable<Dataset | null>}
	 */
	getDefaultDatasetForGovernmentId(governmentId: GovernmentId): Observable<Dataset | null> {
		let obs = of(DatasetSource.None);
		if (governmentId.specificity === GovernmentSpecificity.Government) {
			obs = this.api.getLocalGovernment(governmentId.id as MCAG).pipe(
				map(x => x.financialsDatasetSource)
			);
		} else if (governmentId.specificity === GovernmentSpecificity.GovernmentType) {
			obs = this.api.getGovernmentType(governmentId.id as GovTypeCode).pipe(
				map(x => x.financialsDatasetSource)
			);
		}
		return obs.pipe(
			switchMap(datasetSource => this.getDataset(datasetSource))
		);
	}

	/**
	 * Get the default datasets for each unique dataset source represented in the
	 * list of FIT government types that is associated with a financials dataset source
	 */
	getDefaultFinancialsDatasets() {
		return this.getDefaultDatasets((gt) => gt.financialsDatasetSource != DatasetSource.None);
	}

	/**
	 * Get the default datasets for each unique dataset source represented in the
	 * list of FIT government types that can have financial indicators
	 */
	getDefaultFinancialHealthIndicatorDatasets() {
		return this.getDefaultDatasets((gt) => gt.canHaveIndicators);
	}

	/**
	 * Get the default datasets for each unique dataset source represented in the
	 * list of FIT government types that match the filter condition
	 * @param govTypeFilter GovernmentType lambda expression
	 */
	private getDefaultDatasets(govTypeFilter: (gt: GovernmentType) => boolean) {
		// The unique list of DatasetSources available from `api/GovernmentTypes` endpoint
		const datasetSources = this.api.governmentTypesForList.pipe(
			map(govTypes => {
				const datasetSources = govTypes
					.filter(govTypeFilter)  // Only those government types that match the filter
					.map(x => x.financialsDatasetSource);
				// get unique values by way of Set
				return Array.from(new Set(datasetSources));
			})
		);

		return datasetSources.pipe(
			// make a call for each DatasetSource
			switchMap(datasetSources =>
				forkJoin(
					datasetSources.map(
						dataset => this.getDataset(dataset)
					)
				)
			),
			// remove nulls and erase null with a type guard
			map(datasets => datasets.filter(
				(dataset): dataset is Dataset => dataset !== null
			))
		)
	}

	/**
	 * Get the default year for the provided datasets; latest year common to all datasets
	 *
	 * @param datasetsDetail
	 * @private
	 */
	static getDefaultYearFromDatasets(datasetsDetail: Array<DatasetDetail>) : number {
		const years = intersection(datasetsDetail.map(x => x.detail.includedYears));
		return Math.max(...years);
	}

	/**
	 * Get the default year range for the provided datasets; latest year common to all datasets is the end year
	 * and then default year range size is applied to get the start year
	 *
	 * @param datasetsDetail
	 * @private
	 */
	static getDefaultYearRangeFromDatasets(datasetsDetail: Array<DatasetDetail>) : [number, number] {
		const commonYears = intersection(datasetsDetail.map(x => x.detail.includedYears));
		const allYears = datasetsDetail.map(x => x.detail.includedYears).flat();

		const endYear = Math.max(...commonYears);
		const startYear = Math.max(
				endYear - this.DEFAULT_TREND_YEARS + 1,  // use default year range to determine start date
				Math.min(...allYears) // except if there aren't enough years in the dataset(s)
			);

		return [ startYear, endYear];
	}

	/**
	 * Get default fiscal display year across multiple datasets; use the latest year common to all datasets
	 */
	@Share(ShareMode.Replay)
	getDefaultYear(datasetsDetail: Observable<Array<DatasetDetail>>) : Observable<number> {
		return datasetsDetail.pipe(
			map(datasets => DatasetService.getDefaultYearFromDatasets(datasets))
		);
	}

	/**
	 * Gets the latest common fiscal year for a given array of DatasetSources
	 * @param {(DatasetSource)[]} sources
	 * @returns {Observable<number>}
	 */
	getDefaultYearFromDatasetSources(sources: Array<DatasetSource>): Observable<number> {
		return of(sources).pipe(
			switchMap(sources => zip(
				sources.map(source => this.getDataset(source))
			)),
			switchMap(datasets => zip(
				datasets.filter(x => x !== null).map(dataset => this.getDatasetDetail(dataset.datasetType))
			)),
			map(datasetDetails => DatasetService.getDefaultYearFromDatasets(datasetDetails))
		)
	}

}
